import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useUserAnalytics, useSessionAnalytics } from '../../hooks/useAnalytics';
import MetricCard from '../dashboard/core/MetricCard';
import AreaChart from '../ui/AreaChart';
import BarChart from '../ui/BarChart';
import PieChart from '../ui/PieChart';
import CollapsibleSection from '../ui/CollapsibleSection';
import { transformToChartData, transformToChartDataAsync } from '../../services/chartService';
import { getPreviousPeriodValue } from '../../utils/chartHelpers';
import { FILTER_PERIODS } from '../../constants';
import './UserStats.css';

/**
 * User Stats Component
 * Displays user statistics and analytics
 * @param {Object} props - Component props
 * @param {string} [props.period='day'] - Time period for metrics
 * @param {string|null} [props.platform] - Platform filter
 */
const UserStats = ({ period = FILTER_PERIODS.DAY, platform = null }) => {
  const { data: userData, loading: userLoading, error: userError } = useUserAnalytics(period, platform);
  const { data: sessionData, loading: sessionLoading, error: sessionError } = useSessionAnalytics(period);

  // Generate chart data for key metrics using real data
  const [totalUsersChart, setTotalUsersChart] = useState(null);
  const [activeUsersChart, setActiveUsersChart] = useState(null);
  const [previousUserData, setPreviousUserData] = useState(null);
  const [previousSessionData, setPreviousSessionData] = useState(null);

  // Fetch previous period data for accurate comparison
  useEffect(() => {
    const fetchPreviousPeriodData = async () => {
      try {
        const prevPeriodMap = {
          [FILTER_PERIODS.DAY]: FILTER_PERIODS.DAY,
          [FILTER_PERIODS.WEEK]: FILTER_PERIODS.WEEK,
          [FILTER_PERIODS.MONTH]: FILTER_PERIODS.MONTH,
          [FILTER_PERIODS.YEAR]: FILTER_PERIODS.YEAR,
          [FILTER_PERIODS.ALL]: FILTER_PERIODS.YEAR
        };
        
        const prevPeriod = prevPeriodMap[period] || FILTER_PERIODS.DAY;
        const { getUserAnalytics, getSessionAnalytics } = await import('../../services/firebaseService');
        const [prevUser, prevSession] = await Promise.all([
          getUserAnalytics(prevPeriod, platform).catch(() => null),
          getSessionAnalytics(prevPeriod).catch(() => null)
        ]);
        
        setPreviousUserData(prevUser);
        setPreviousSessionData(prevSession);
      } catch (error) {
        setPreviousUserData(null);
        setPreviousSessionData(null);
      }
    };

    if (userData && sessionData) {
      fetchPreviousPeriodData();
    }
  }, [period, platform, userData, sessionData]);

  useEffect(() => {
    if (!userData || !sessionData) {
      setTotalUsersChart(null);
      setActiveUsersChart(null);
      return;
    }

    // Use actual previous period data if available, otherwise calculate from growth or use estimate
    const currentTotal = userData.totalUserCount || 0;
    const previousTotal = getPreviousPeriodValue(userData, previousUserData, 'totalUserCount', 0.95);

    const currentActive = sessionData.activeUsers || 0;
    const previousActive = getPreviousPeriodValue(sessionData, previousSessionData, 'activeUsers', 0.9);

    // Try to fetch real data from users collection
    Promise.all([
      transformToChartDataAsync(
        { totalUserCount: currentTotal },
        { totalUserCount: previousTotal },
        'totalUserCount',
        period,
        {
          collectionName: 'users',
          valueExtractor: () => 1 // Count users
        }
      ),
      transformToChartDataAsync(
        { activeUsers: currentActive },
        { activeUsers: previousActive },
        'activeUsers',
        period,
        {
          collectionName: 'users',
          valueExtractor: () => 1 // Count active users
        }
      )
    ]).then(([totalChart, activeChart]) => {
      setTotalUsersChart(totalChart);
      setActiveUsersChart(activeChart);
    }).catch(() => {
      // Fallback to synchronous generated data
      setTotalUsersChart(transformToChartData(
        { totalUserCount: currentTotal },
        { totalUserCount: previousTotal },
        'totalUserCount',
        period
      ));
      setActiveUsersChart(transformToChartData(
        { activeUsers: currentActive },
        { activeUsers: previousActive },
        'activeUsers',
        period
      ));
    });
  }, [userData, sessionData, previousUserData, previousSessionData, period]);

  const isLoading = userLoading || sessionLoading;
  const hasError = userError || sessionError;

  if (hasError) {
    return (
      <div className="user-stats error">
        <h3>Error loading user stats</h3>
        <p>Please try refreshing the page</p>
      </div>
    );
  }

  return (
    <div className="user-stats">
      <h2 className="section-title"><i className="bi bi-people icon-brand" style={{ marginRight: 8 }}></i> User Statistics</h2>
      
      {/* Key Metrics - Always Visible */}
      <div className="stats-grid">
        <MetricCard
          title="Total Users"
          value={typeof userData?.totalUserCount === 'number' ? userData.totalUserCount : 0}
          formatType="number"
          loading={userLoading}
          icon={<i className="bi bi-people icon-brand"></i>}
          color="blue"
          subtitle={`${period} period`}
        />
        <MetricCard
          title="Verified Users"
          value={typeof userData?.verifiedUsers === 'number' ? userData.verifiedUsers : 0}
          formatType="number"
          loading={userLoading}
          icon={<i className="bi bi-patch-check icon-brand"></i>}
          color="green"
          subtitle={`${((userData?.verifiedUsers || 0) / (userData?.totalUserCount || 1) * 100).toFixed(1)}%`}
        />
        <MetricCard
          title="Active Users"
          value={typeof sessionData?.activeUsers === 'number' ? sessionData.activeUsers : 0}
          formatType="number"
          loading={sessionLoading}
          icon={<i className="bi bi-lightning-charge icon-brand"></i>}
          color="orange"
          subtitle="recently active"
        />
      </div>

      {/* Additional Metrics - Collapsible */}
      <CollapsibleSection
        title="Additional User Details"
        initialVisible={3}
        defaultExpanded={false}
      >
        {[
          <MetricCard
            key="onboarded"
            title="Onboarded Users"
            value={typeof userData?.onboardedUsers === 'number' ? userData.onboardedUsers : 0}
            formatType="number"
            loading={userLoading}
            icon={<i className="bi bi-rocket-takeoff icon-brand"></i>}
            color="purple"
            subtitle={`${((userData?.onboardedUsers || 0) / (userData?.totalUserCount || 1) * 100).toFixed(1)}%`}
          />,
          <MetricCard
            key="wallet"
            title="Wallet Users"
            value={typeof userData?.walletUsers === 'number' ? userData.walletUsers : 0}
            formatType="number"
            loading={userLoading}
            icon={<i className="bi bi-wallet2 icon-brand"></i>}
            color="teal"
            subtitle={`${((userData?.walletUsers || 0) / (userData?.totalUserCount || 1) * 100).toFixed(1)}%`}
          />,
          <MetricCard
            key="verified"
            title="Recently Verified"
            value={typeof sessionData?.recentlyVerifiedUsers === 'number' ? sessionData.recentlyVerifiedUsers : 0}
            formatType="number"
            loading={sessionLoading}
            icon={<i className="bi bi-shield-lock icon-brand"></i>}
            color="indigo"
            subtitle="verified this period"
          />
        ]}
      </CollapsibleSection>

      {/* Chart sections - Using diverse visualizations */}
      {totalUsersChart && (
        <div className="chart-container-compact">
          <AreaChart
            data={totalUsersChart.data}
            comparisonData={totalUsersChart.comparisonData}
            label="Total Users Over Time"
            formatType="number"
            color="blue"
          />
        </div>
      )}

      {activeUsersChart && (
        <div className="chart-container-compact">
          <AreaChart
            data={activeUsersChart.data}
            comparisonData={activeUsersChart.comparisonData}
            label="Active Users Over Time"
            formatType="number"
            color="orange"
          />
        </div>
      )}

      {/* Platform distribution - Compact */}
      {userData && (userData.iosUsers || userData.androidUsers || userData.solanaUsers) && (
        <div className="chart-container-compact">
          <PieChart
            data={[
              { label: 'iOS', value: userData.iosUsers || 0 },
              { label: 'Android', value: userData.androidUsers || 0 },
              { label: 'Solana', value: userData.solanaUsers || 0 }
            ].filter(item => item.value > 0)}
            label="Users by Platform"
            formatType="number"
            showPercentages={true}
            innerRadius={0.5}
          />
        </div>
      )}

      {/* User verification status - Compact */}
      {userData && (
        <div className="chart-container-compact">
          <BarChart
            data={[
              { label: 'Verified', value: userData.verifiedUsers || 0 },
              { label: 'Unverified', value: (userData.totalUserCount || 0) - (userData.verifiedUsers || 0) }
            ].filter(item => item.value > 0)}
            label="Verification Status"
            formatType="number"
            orientation="vertical"
          />
        </div>
      )}
    </div>
  );
};

UserStats.propTypes = {
  period: PropTypes.string,
  platform: PropTypes.string
};

export default UserStats;
